import type { AnyRouter, BlockerFn } from "@tanstack/react-router";

import { MARKETING_HOST } from "./hosts";

/**
 * Whether this document is the marketing site, where a link to another page
 * loads that page as a new document, as every link on the published site did.
 * Never on the server, and never on any other host.
 */
export function navigatesByDocument(): boolean {
  return (
    typeof window !== "undefined" && window.location.host === MARKETING_HOST
  );
}

/**
 * The blocker that hands a move to another page to `load` instead of the
 * router. A new query or fragment on the same page, and the browser's back and
 * forward, stay the router's.
 */
export function toOtherPages(
  load: (href: string, replace: boolean) => void
): BlockerFn {
  return ({ currentLocation, nextLocation, action }) => {
    const leaves =
      (action === "PUSH" || action === "REPLACE") &&
      currentLocation.pathname !== nextLocation.pathname;
    if (leaves) load(nextLocation.href, action === "REPLACE");
    return leaves;
  };
}

/**
 * On the marketing host a link to another page loads it, rather than drawing
 * it in place. The code the marketing team adds to a page in the CMS
 * (`server/cmsCustomCode.mjs`) is printed into that page's HTML and runs when
 * the document loads, as Webflow's did: a page drawn in place would keep the
 * last page's code, its styles included, and never run its own. Nothing is
 * fetched ahead for a link, since the page it opens is loaded whole.
 */
export function loadPagesAsDocuments(router: AnyRouter): void {
  router.update({ ...router.options, defaultPreload: false });
  router.history.block({
    blockerFn: toOtherPages((href, replace) =>
      replace ? window.location.replace(href) : window.location.assign(href)
    ),
    enableBeforeUnload: false,
  });
}
